// Per-domain "Funnel" overview (FF-22 / MIS-1005) — replaces the MIS-813 stub.
//
// The "Open funnel" destination from the wizard's review step (FF-21). KEEPS
// the route path (/domains/$domainId/analytics) — "Funnel" is the page TITLE,
// not a new route (funnel == domain aggregate; nothing is persisted for it).
// Four sections for ONE domain:
//   1. the computed setup checklist — FF-21's ReviewChecklist, which reads
//      FF-15's shared ['setup-status', domainId] cache slot. This page's
//      routing/binding badges (section 4) consume the SAME per-domain query via
//      useSetupStatus — React Query dedupes, so there is exactly one
//      setup-status fetch and never one per lander row.
//   2. funnel metric cards — GET /api/analytics/funnel?domainId= rendered by
//      the shared FunnelCards.
//   3. per-campaign breakdown — GET /api/analytics/by-campaign?domainId=
//      rendered by CampaignsTable (campaign, visits, leads, calls, conv,
//      revenue, spend, ROAS — the FF-12 row shape). Sections 2–3 share ONE
//      date-range select (the analytics index's preset → concrete ET-day
//      window conversion via lib/analytics). The window label under the table
//      is the SHARED EtWindowLabel (components/analytics) so the by-campaign
//      meta's QF-1 `scope` flags render the same all-time disclosure as the
//      main Campaigns tab (calls/revenue/spend are all-time per campaign —
//      only visits/leads honor the window).
//   4. the domain's landers — routing mode + FB-binding badge derived from the
//      setup-status payload's checks.numberRouting.perLander / checks.fbBinding
//      (binding is domain-granular — boundLanders is a count), an "Edit design"
//      deep link when the lander references a Puck template (the builder param
//      is the TEMPLATE id — FF-26; templateId null + materialized puckJson is a
//      per-lander snapshot, hint only), and a Clone action opening FF-24's
//      CloneLanderDialog (this page owns that import).
//
// All endpoints via the typed `useApi()` hc<AppType> client; cents→dollars at
// the render edge only (inside FunnelCards/CampaignsTable); aggregates only —
// no lead PII here.

import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';
import { CampaignsTable } from '@/components/analytics/CampaignsTable';
import { EtWindowLabel } from '@/components/analytics/EtWindowLabel';
import { FunnelCards } from '@/components/analytics/FunnelCards';
import { ReviewChecklist } from '@/components/funnels/ReviewChecklist';
import { useSetupStatus } from '@/components/funnels/useSetupStatus';
import { CloneLanderDialog } from '@/components/landers/CloneLanderDialog';
import type { Lander } from '@/components/landers/types';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/toast';
import { type DateRange, formatRange, presetToRange, type RangePreset } from '@/lib/analytics';
import { useApi } from '@/lib/api';
import { useTenantContext } from '@/lib/auth';

export const Route = createFileRoute('/_authenticated/domains/$domainId/analytics')({
  component: DomainFunnelPage,
});

const RANGE_OPTIONS: { value: RangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: 'custom', label: 'Custom' },
];

/** Build the `from`/`to` query fragment, dropping undefined edges. */
function rangeQuery(range: DateRange | undefined): { from?: string; to?: string } {
  if (!range?.from || !range?.to) return {};
  return { from: range.from, to: range.to };
}

// Per-lander routing-mode pill, keyed by the setup-status perLander `mode`
// ('pool' | 'static' | 'disabled' own config, 'default' domain fallback,
// 'missing' nothing resolves — see apps/worker/src/routes/setup_status.ts).
const ROUTING_BADGES: Record<string, { variant: BadgeVariant; label: string }> = {
  pool: { variant: 'green', label: 'Pool' },
  static: { variant: 'green', label: 'Static' },
  default: { variant: 'gray', label: 'Domain default' },
  disabled: { variant: 'gray', label: 'Disabled' },
  missing: { variant: 'red', label: 'No routing' },
};

// Exported for the colocated test (the ad/routes pattern): the route file's
// component renders directly with the router seam mocked.
export function DomainFunnelPage(): React.ReactElement {
  const { domainId } = Route.useParams();
  const domainIdNum = Number(domainId);
  const api = useApi();
  const { toast } = useToast();
  const { activeTenantId } = useTenantContext();

  const [preset, setPreset] = useState<RangePreset>('7d');
  const [customRange, setCustomRange] = useState<DateRange>({ from: '', to: '' });
  // FF-24: the clone dialog's source row (mounted per-source so its state keys
  // off the row, mirroring landers/index.tsx).
  const [pendingClone, setPendingClone] = useState<Lander | null>(null);

  // Resolve the active preset to a concrete ET-day window. `custom` uses the
  // two date inputs (only applied once both are filled).
  const range = useMemo<DateRange | undefined>(() => {
    if (preset === 'custom') {
      return customRange.from && customRange.to ? customRange : undefined;
    }
    return presetToRange(preset);
  }, [preset, customRange]);

  // ONE per-domain setup-status query — the same ['setup-status', domainId]
  // slot ReviewChecklist (FF-15/FF-21) populates, so the landers section's
  // badges add no bespoke fetch (and never fetch per row).
  const { data: status } = useSetupStatus(domainIdNum);

  const funnelQuery = useQuery({
    queryKey: ['analytics-funnel', activeTenantId, domainId, range],
    queryFn: async () => {
      const res = await api.api.analytics.funnel.$get({
        query: { ...rangeQuery(range), domainId },
      });
      if (!res.ok) throw new Error(`Funnel request failed (${res.status})`);
      return (await res.json()).funnel;
    },
  });

  // Keeps the full `{ rows, meta }` envelope so the section can render the
  // meta ET-day window label (mirrors the analytics index's campaigns tab).
  const campaignsQuery = useQuery({
    queryKey: ['analytics-by-campaign', activeTenantId, domainId, range],
    queryFn: async () => {
      const res = await api.api.analytics['by-campaign'].$get({
        query: { ...rangeQuery(range), domainId },
      });
      if (!res.ok) throw new Error(`Campaigns request failed (${res.status})`);
      return await res.json();
    },
  });

  // Same key as domains/$domainId/landers.tsx so the cache slot is shared.
  const landersQuery = useQuery({
    queryKey: ['landers', 'byDomain', domainId],
    queryFn: async () => {
      const res = await api.api.landers.$get({ query: { domainId } });
      if (!res.ok) throw new Error('Failed to load landers');
      return (await res.json()).landers;
    },
  });

  // Surface fetch failures as toasts, keyed on the message so each distinct
  // failure fires once (the analytics index pattern).
  const funnelErrorMsg = funnelQuery.error?.message;
  const campaignsErrorMsg = campaignsQuery.error?.message;
  const landersErrorMsg = landersQuery.error?.message;
  useEffect(() => {
    if (funnelErrorMsg) {
      toast({
        title: 'Could not load funnel',
        description: funnelErrorMsg,
        variant: 'destructive',
      });
    }
  }, [funnelErrorMsg, toast]);
  useEffect(() => {
    if (campaignsErrorMsg) {
      toast({
        title: 'Could not load campaigns',
        description: campaignsErrorMsg,
        variant: 'destructive',
      });
    }
  }, [campaignsErrorMsg, toast]);
  useEffect(() => {
    if (landersErrorMsg) {
      toast({
        title: 'Could not load landers',
        description: landersErrorMsg,
        variant: 'destructive',
      });
    }
  }, [landersErrorMsg, toast]);

  // landerId → routing mode, from the ONE setup-status payload.
  const routingByLander = useMemo(() => {
    const map = new Map<number, string>();
    for (const p of status?.checks.numberRouting.perLander ?? []) map.set(p.landerId, p.mode);
    return map;
  }, [status]);

  const landerColumns = useMemo<ColumnDef<Lander, unknown>[]>(
    () => [
      {
        accessorKey: 'slug',
        header: 'Slug',
        cell: ({ row }) => (
          <Link
            to="/landers/$landerId/edit"
            params={{ landerId: String(row.original.id) }}
            className="font-mono font-medium text-gray-900 hover:underline"
          >
            {row.original.slug}
          </Link>
        ),
      },
      {
        id: 'routing',
        header: 'Routing',
        cell: ({ row }) => {
          if (!status) return <span className="text-xs text-gray-400">—</span>;
          // Unlisted in perLander ⇔ the aggregator didn't see this lander —
          // treat as missing rather than vacuously routed (FF-23 rule).
          const mode = routingByLander.get(row.original.id) ?? 'missing';
          const badge = ROUTING_BADGES[mode] ?? { variant: 'gray' as BadgeVariant, label: mode };
          return <Badge variant={badge.variant}>{badge.label}</Badge>;
        },
      },
      {
        id: 'binding',
        header: 'FB binding',
        cell: () => {
          if (!status) return <span className="text-xs text-gray-400">—</span>;
          // Binding is domain-granular in setup-status (boundLanders is a
          // count), so every row reflects the domain's check.
          return status.checks.fbBinding.ok ? (
            <Badge variant="green">Bound</Badge>
          ) : (
            <Badge variant="yellow">Not bound</Badge>
          );
        },
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            {/* FF-26 — "Edit design" deep link to the Puck builder. The param
                is the TEMPLATE id (live reference), never the lander id. */}
            {row.original.templateId != null ? (
              <Link to="/templates/$id/builder" params={{ id: String(row.original.templateId) }}>
                <Button variant="ghost" size="sm">
                  Edit design
                </Button>
              </Link>
            ) : row.original.puckJson != null ? (
              <span
                className="text-xs text-gray-400"
                title="per-lander snapshot — edit via template"
              >
                snapshot
              </span>
            ) : null}
            {/* FF-24: clone this lander (same or another domain, 1–20 copies). */}
            <Button variant="ghost" size="sm" onClick={() => setPendingClone(row.original)}>
              Clone
            </Button>
          </div>
        ),
      },
    ],
    [status, routingByLander],
  );

  return (
    <div className="space-y-8 p-8">
      <div>
        <Link
          to="/domains/$domainId"
          params={{ domainId }}
          className="text-sm text-gray-500 hover:underline"
        >
          ← Domain
        </Link>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-gray-900">Funnel</h1>
        <p className="text-sm text-gray-500">
          {status ? `${status.host} — ` : ''}setup, performance and landers for this domain —{' '}
          {formatRange(preset, range)} (ET).
        </p>
      </div>

      {/* 1 — setup checklist (FF-21's component, compact review surface). */}
      <section aria-label="Setup checklist" className="max-w-2xl space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Setup checklist</h2>
        <ReviewChecklist domainId={domainIdNum} />
      </section>

      {/* Shared date-range select — drives BOTH the funnel cards and the
          campaigns table below. */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-40">
          <Label htmlFor="range">Range</Label>
          <Select
            id="range"
            className="mt-1"
            value={preset}
            onChange={(e) => setPreset(e.target.value as RangePreset)}
          >
            {RANGE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
        </div>

        {preset === 'custom' ? (
          <>
            <div className="w-40">
              <Label htmlFor="from">From</Label>
              <Input
                id="from"
                type="date"
                className="mt-1"
                value={customRange.from}
                onChange={(e) => setCustomRange((r) => ({ ...r, from: e.target.value }))}
              />
            </div>
            <div className="w-40">
              <Label htmlFor="to">To</Label>
              <Input
                id="to"
                type="date"
                className="mt-1"
                value={customRange.to}
                onChange={(e) => setCustomRange((r) => ({ ...r, to: e.target.value }))}
              />
            </div>
          </>
        ) : null}
      </div>

      {/* 2 — funnel metric cards. */}
      <section aria-label="Funnel metrics" className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Performance</h2>
        {funnelQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading funnel…</p>
        ) : funnelQuery.data ? (
          <FunnelCards funnel={funnelQuery.data} />
        ) : (
          <p className="text-sm text-gray-500">No funnel data for this range.</p>
        )}
      </section>

      {/* 3 — per-campaign breakdown (same range as the cards). */}
      <section aria-label="Campaigns" className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Campaigns</h2>
        {campaignsQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading campaigns…</p>
        ) : (
          <div className="space-y-3">
            <CampaignsTable rows={campaignsQuery.data?.rows ?? []} />
            {campaignsQuery.data ? <EtWindowLabel meta={campaignsQuery.data.meta} /> : null}
          </div>
        )}
      </section>

      {/* 4 — landers with routing/binding state + design/clone actions. */}
      <section aria-label="Landers" className="space-y-3">
        <h2 className="text-base font-semibold text-gray-900">Landers</h2>
        {landersQuery.isLoading ? (
          <p className="text-sm text-gray-500">Loading landers…</p>
        ) : landersQuery.isError ? (
          <p className="text-sm text-red-600">Failed to load landers. Try again.</p>
        ) : (
          <DataTable
            columns={landerColumns}
            data={landersQuery.data ?? []}
            emptyMessage="No landers on this domain yet."
          />
        )}
      </section>

      {/* Mounted per-source so the dialog's queries/state key off the row. */}
      {pendingClone !== null && (
        <CloneLanderDialog
          landerId={pendingClone.id}
          sourceDomainId={pendingClone.domainId}
          open
          onOpenChange={(isOpen) => !isOpen && setPendingClone(null)}
        />
      )}
    </div>
  );
}
